import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { settlementApi } from '../api';
import {
  SETTLEMENT_STATUSES, SETTLEMENT_STATUS_COLORS,
  SETTLEMENT_DATE_CRITERIA, CUSTOMER_TYPES, CUSTOMER_TYPE_COLORS,
} from '../constants';

const PAGE_SIZE = 20;

export default function MakeupShopSettlements() {
  // 필터
  const [settlementStatus, setSettlementStatus] = useState('');
  const [dateCriteria, setDateCriteria] = useState('orderDt');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');

  // 데이터
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // 요약
  const [summary, setSummary] = useState({ settledCount: 0, settledAmount: 0, pendingCount: 0, pendingAmount: 0 });

  // 체크박스
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // 일괄 정산일
  const [bulkSettleDate, setBulkSettleDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await settlementApi.list({
        settlementStatus: settlementStatus || undefined,
        dateCriteria,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        vendorName: vendorSearch || undefined,
        page,
        size: PAGE_SIZE,
      });
      setItems(result.items || []);
      setTotal(result.total || 0);
      setSummary({
        settledCount: result.settledCount || 0,
        settledAmount: result.settledAmount || 0,
        pendingCount: result.pendingCount || 0,
        pendingAmount: result.pendingAmount || 0,
      });
    } catch (err) {
      console.error('정산 목록 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [settlementStatus, dateCriteria, dateFrom, dateTo, vendorSearch, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 체크박스 핸들러
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((item: any) => item.makeupSettlementIdx)));
    }
  };

  // 일괄 정산처리
  const handleBulkSettle = async () => {
    if (selectedIds.size === 0) return;
    if (!bulkSettleDate) { alert('정산일을 선택해주세요.'); return; }
    if (!confirm(`${selectedIds.size}건을 정산처리 하시겠습니까?`)) return;
    try {
      await settlementApi.bulkSettle(Array.from(selectedIds), bulkSettleDate);
      setSelectedIds(new Set());
      fetchData();
    } catch (err: any) {
      alert(`정산처리 실패: ${err.message}`);
    }
  };

  // 일괄 정산취소
  const handleBulkCancel = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`${selectedIds.size}건의 정산을 취소하시겠습니까?`)) return;
    try {
      await settlementApi.bulkCancel(Array.from(selectedIds));
      setSelectedIds(new Set());
      fetchData();
    } catch (err: any) {
      alert(`정산취소 실패: ${err.message}`);
    }
  };

  const formatDate = (dt?: string) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return Number(amount).toLocaleString('ko-KR');
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8 pb-28">

        {/* 헤더 */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">업체고객 매출관리 (정산 관리)</h1>
          <p className="text-sm text-gray-600 mt-1">파트너 업체별 정산 현황을 관리합니다</p>
        </div>

        {/* 필터 영역 */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6 space-y-3">
          {/* 정산여부 */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-600 w-[72px] flex-shrink-0">정산여부</label>
            <select value={settlementStatus} onChange={(e) => { setSettlementStatus(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {SETTLEMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* 조회기간 */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm font-medium text-gray-600 w-[72px] flex-shrink-0">조회기간</label>
            <div className="flex items-center gap-2">
              {SETTLEMENT_DATE_CRITERIA.map(c => (
                <label key={c.value} className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="dateCriteria" value={c.value}
                    checked={dateCriteria === c.value}
                    onChange={() => { setDateCriteria(c.value); setPage(1); }}
                    className="accent-blue-600" />
                  <span className="text-sm text-gray-700">{c.label}</span>
                </label>
              ))}
            </div>
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span className="text-gray-400">~</span>
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          {/* 업체조회 */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-600 w-[72px] flex-shrink-0">업체조회</label>
            <input type="text" placeholder="파트너 업체명 검색" value={vendorSearch}
              onChange={(e) => { setVendorSearch(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[220px]" />
          </div>
        </div>

        {/* 요약 */}
        <div className="flex items-center gap-4 mb-4 text-sm">
          <span className="text-gray-700">
            총 <span className="font-bold text-gray-900">{total}</span>건
          </span>
          <span className="text-gray-300">|</span>
          <span className="text-green-700">
            정산완료 <span className="font-bold">{summary.settledCount}</span>건
            <span className="ml-1 text-xs">({formatAmount(summary.settledAmount)}원)</span>
          </span>
          <span className="text-gray-300">|</span>
          <span className="text-red-600">
            미정산 <span className="font-bold">{summary.pendingCount}</span>건
            <span className="ml-1 text-xs">({formatAmount(summary.pendingAmount)}원)</span>
          </span>
        </div>

        {/* 테이블 */}
        {loading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center text-gray-400">
            <i className="ri-money-dollar-circle-line text-5xl block mb-3"></i>정산 내역이 없습니다.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs">
                    <th className="px-3 py-3 text-center font-medium w-10">
                      <input type="checkbox" checked={items.length > 0 && selectedIds.size === items.length}
                        onChange={toggleSelectAll} className="accent-blue-600 cursor-pointer" />
                    </th>
                    <th className="px-3 py-3 text-left font-medium">업체명</th>
                    <th className="px-3 py-3 text-left font-medium">고객명</th>
                    <th className="px-3 py-3 text-center font-medium">구분</th>
                    <th className="px-3 py-3 text-center font-medium">리허설일</th>
                    <th className="px-3 py-3 text-center font-medium">본식일</th>
                    <th className="px-3 py-3 text-right font-medium">계약금액</th>
                    <th className="px-3 py-3 text-right font-medium">수수료율</th>
                    <th className="px-3 py-3 text-right font-medium">수수료</th>
                    <th className="px-3 py-3 text-right font-medium">정산금액</th>
                    <th className="px-3 py-3 text-center font-medium">정산상태</th>
                    <th className="px-3 py-3 text-center font-medium">정산일</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item: any) => {
                    const customerType = CUSTOMER_TYPES.find(c => c.value === item.customerType);
                    const typeColor = CUSTOMER_TYPE_COLORS[item.customerType] || 'bg-gray-100 text-gray-700';
                    const statusColor = SETTLEMENT_STATUS_COLORS[item.settlementStatus] || 'bg-gray-100 text-gray-700';

                    return (
                      <tr key={item.makeupSettlementIdx} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={selectedIds.has(item.makeupSettlementIdx)}
                            onChange={() => toggleSelect(item.makeupSettlementIdx)}
                            className="accent-blue-600 cursor-pointer" />
                        </td>
                        <td className="px-3 py-3 text-gray-800 font-medium">{item.partnerVendorName || '-'}</td>
                        <td className="px-3 py-3">
                          <div className="flex flex-col">
                            {item.groomName && <span className="text-xs"><i className="ri-men-line text-blue-500 mr-1"></i>{item.groomName}</span>}
                            {item.brideName && <span className="text-xs"><i className="ri-women-line text-pink-500 mr-1"></i>{item.brideName}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${typeColor}`}>
                            {customerType?.label || item.customerType || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center text-xs text-gray-600">{formatDate(item.rehearsalDt)}</td>
                        <td className="px-3 py-3 text-center text-xs text-gray-600">{formatDate(item.weddingDt)}</td>
                        <td className="px-3 py-3 text-right text-gray-800">{formatAmount(item.totalSalesAmount)}원</td>
                        <td className="px-3 py-3 text-right text-gray-600">{item.commissionRate != null ? `${item.commissionRate}%` : '-'}</td>
                        <td className="px-3 py-3 text-right text-gray-600">{formatAmount(item.commissionAmount)}원</td>
                        <td className="px-3 py-3 text-right font-semibold text-gray-800">{formatAmount(item.settlementAmount)}원</td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColor}`}>
                            {item.settlementStatus === 'SETTLED' ? '정산' : '미정산'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center text-xs text-gray-600">{formatDate(item.settledDt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
              const startPage = Math.max(1, Math.min(page - 4, totalPages - 9));
              return startPage + i;
            }).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-sm cursor-pointer ${page === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}

        {/* 일괄 정산 액션바 (하단 고정) */}
        {selectedIds.size > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
            <div className="max-w-screen-xl mx-auto px-8 py-4 flex items-center justify-between">
              <span className="text-sm text-gray-700">
                체크하신 발주상품 <span className="font-bold text-blue-600">{selectedIds.size}</span>건
              </span>
              <div className="flex items-center gap-3">
                <input type="date" value={bulkSettleDate}
                  onChange={(e) => setBulkSettleDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <button onClick={handleBulkSettle}
                  className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                  정산처리
                </button>
                <button onClick={handleBulkCancel}
                  className="px-5 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors cursor-pointer">
                  정산취소
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
